import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Home, Music, Trophy, Coins, Cake, Loader2, ListMusic, Info, Save, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useGuild } from '@/contexts/GuildContext';
import {
  API, authHeader, inputCls, labelCls, cardCls, btnPrimary, fmtDuration, MONTHS,
  PageShell, PageLoading, EmptyState, Avatar,
} from '@/lib/pageKit';

function Bar({ value, max }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="h-2 rounded-full bg-[var(--bg-hover-md,var(--bg-hover))] overflow-hidden">
      <div className="h-full bg-[#5865F2] rounded-full transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
    </div>
  );
}

function NowPlayingCard({ np, queue, requests }) {
  const [elapsed, setElapsed] = useState(np?.elapsed || 0);
  useEffect(() => { setElapsed(np?.elapsed || 0); }, [np?.elapsed, np?.title]);
  useEffect(() => {
    if (!np?.playing || np?.paused) return undefined;
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [np?.playing, np?.paused, np?.title]);

  const active = np && np.playing;
  const shown = np?.duration ? Math.min(elapsed, np.duration) : elapsed;

  return (
    <div className={`${cardCls} p-5`}>
      <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] mb-4 flex items-center gap-2"><Music size={16} className="text-[#5865F2]" /> Now playing</h2>
      {!active ? (
        <p className="text-[var(--text-muted)] text-sm font-[Manrope]">Nothing is playing right now.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3">
            {np.thumbnail ? (
              <img src={np.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-[#5865F2]/15 flex items-center justify-center flex-shrink-0"><Music size={22} className="text-[#5865F2]" /></div>
            )}
            <div className="min-w-0">
              {np.url ? (
                <a href={np.url} target="_blank" rel="noopener noreferrer" className="text-[var(--text-primary)] text-sm font-semibold font-[Outfit] truncate block hover:underline">{np.title}</a>
              ) : (
                <p className="text-[var(--text-primary)] text-sm font-semibold font-[Outfit] truncate">{np.title}</p>
              )}
              <p className="text-[var(--text-muted)] text-xs font-[Manrope] truncate">{np.uploader}{np.paused ? ' · Paused' : ''}</p>
            </div>
          </div>
          {np.duration > 0 && (
            <>
              <Bar value={shown} max={np.duration} />
              <div className="flex justify-between text-[11px] text-[var(--text-muted)] font-mono mt-1">
                <span>{fmtDuration(shown)}</span><span>{fmtDuration(np.duration)}</span>
              </div>
            </>
          )}
        </>
      )}
      {queue.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--border)]">
          <p className={labelCls}>Up next</p>
          <ul className="space-y-1">
            {queue.slice(0, 5).map((q, i) => (
              <li key={i} className="flex items-center gap-2 text-xs font-[Manrope]">
                <span className="text-[var(--text-muted)] w-4">{i + 1}</span>
                <span className="text-[var(--text-secondary)] truncate flex-1">{q.title}</span>
                {q.duration ? <span className="text-[var(--text-muted)] font-mono">{fmtDuration(q.duration)}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {requests?.enabled !== false && (
        <Link to="/song-requests" className="mt-4 inline-flex items-center gap-2 text-sm text-[#5865F2] hover:underline font-[Manrope]">
          <ListMusic size={14} /> Request a song
        </Link>
      )}
    </div>
  );
}

function BirthdayCard({ guildId, initial, onChanged }) {
  const [month, setMonth] = useState(initial?.month || 1);
  const [day, setDay] = useState(initial?.day || 1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initial) { setMonth(initial.month); setDay(initial.day); } }, [initial?.month, initial?.day]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/member/${guildId}/birthday`, { month: Number(month), day: Number(day) }, authHeader());
      toast.success('Birthday saved');
      onChanged({ month: Number(month), day: Number(day) });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save birthday');
    } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/api/member/${guildId}/birthday`, authHeader());
      toast.success('Birthday cleared');
      onChanged(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear birthday');
    } finally { setBusy(false); }
  };

  return (
    <div className={`${cardCls} p-5`}>
      <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] mb-1 flex items-center gap-2"><Cake size={16} className="text-pink-400" /> Birthday</h2>
      <p className="text-[var(--text-muted)] text-xs font-[Manrope] mb-4">
        {initial ? `Saved: ${MONTHS[initial.month - 1]} ${initial.day}` : 'Set your birthday so the server can celebrate with you.'}
      </p>
      <div className="grid grid-cols-[1fr_90px] gap-3 mb-4">
        <div>
          <label className={labelCls}>Month</label>
          <select value={month} onChange={e => setMonth(e.target.value)} className={inputCls}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Day</label>
          <input type="number" min={1} max={31} value={day} onChange={e => setDay(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className={btnPrimary}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        {initial && (
          <button onClick={clear} disabled={busy}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:text-red-400 hover:bg-red-500/10 text-sm font-[Manrope] disabled:opacity-50 transition-colors">
            <Trash2 size={14} /> Clear
          </button>
        )}
      </div>
    </div>
  );
}

function Leaderboards({ data }) {
  const [tab, setTab] = useState('xp');
  const rows = (tab === 'xp' ? data?.xp : data?.coins) || [];
  return (
    <div className={`${cardCls} p-5`}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] flex items-center gap-2"><Trophy size={16} className="text-amber-400" /> Leaderboards</h2>
        <div className="flex items-center gap-1 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-1">
          {[['xp', 'XP'], ['coins', 'Coins']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1 rounded-lg text-sm font-medium font-[Manrope] transition-colors ${tab === k ? 'bg-[#5865F2] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-[var(--text-muted)] text-sm font-[Manrope] text-center py-4">Nothing here yet</p>
      ) : (
        <ol className="divide-y divide-[var(--border)]">
          {rows.slice(0, 10).map((u, i) => (
            <li key={u.user_id} className="flex items-center gap-3 py-2.5">
              <span className="w-6 text-center text-sm font-mono text-[var(--text-muted)]">{i + 1}</span>
              <Avatar userId={u.user_id} avatar={u.avatar} name={u.username} />
              <span className="flex-1 min-w-0 truncate text-[var(--text-primary)] text-sm font-[Manrope]">{u.username || u.user_id}</span>
              <span className="text-sm font-semibold font-[Manrope] text-[var(--text-secondary)]">
                {tab === 'xp' ? `Lv ${u.level} · ${(u.xp ?? 0).toLocaleString()} XP` : (u.balance ?? 0).toLocaleString()}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function MemberHub() {
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const [overview, setOverview] = useState(null);
  const [boards, setBoards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [birthday, setBirthday] = useState(null);

  const loadOverview = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/api/member/${selectedGuildId}/overview`, authHeader());
      setOverview(r.data);
      setBirthday(r.data?.profile?.birthday || null);
    } catch (e) {
      if (!overview) toast.error(e.response?.data?.detail || 'Failed to load your member overview');
    } finally {
      setLoading(false);
    }
  }, [selectedGuildId, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    setOverview(null);
    loadOverview();
    const id = setInterval(loadOverview, 10000);
    return () => clearInterval(id);
  }, [loadOverview]);

  useEffect(() => {
    if (!selectedGuildId || isGlobal) return;
    axios.get(`${API}/api/member/${selectedGuildId}/leaderboards`, authHeader())
      .then(r => setBoards(r.data)).catch(() => setBoards({ xp: [], coins: [] }));
  }, [selectedGuildId, isGlobal]);

  if (loading) return <PageLoading />;

  const p = overview?.profile;
  const user = overview?.user;
  const tier = overview?.tier || 'member';

  return (
    <PageShell
      icon={Home} title="Member Hub"
      subtitle={selectedGuild ? `Your activity in ${selectedGuild.name}` : 'Your activity'}
      testId="member-hub-main" isGlobal={isGlobal} maxW="max-w-5xl"
    >
      <div className="flex items-start gap-3 bg-[#5865F2]/10 border border-[#5865F2]/20 rounded-xl px-4 py-3 text-sm font-[Manrope] text-[var(--text-secondary)]">
        <Info size={16} className="mt-0.5 flex-shrink-0 text-[#5865F2]" />
        <span>
          You have <strong className="text-[var(--text-primary)]">member access</strong>{tier !== 'member' ? ` (${tier})` : ''}. Here you can see your profile, request songs and set your birthday.
          Server staff can unlock more pages such as stats, giveaways, tickets, economy and moderation tools.
        </span>
      </div>

      {!overview ? (
        <EmptyState icon={Home} title="Couldn't load your overview" hint="Try refreshing the page" />
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className={`${cardCls} p-5`}>
              <div className="flex items-center gap-3 mb-5">
                <Avatar userId={user?.id} avatar={user?.avatar} name={user?.username} size={48} />
                <div className="min-w-0">
                  <p className="text-[var(--text-primary)] font-semibold font-[Outfit] truncate">{user?.username}</p>
                  <p className="text-[var(--text-muted)] text-xs font-[Manrope] capitalize">{tier}</p>
                </div>
                {p?.rank ? (
                  <span className="ml-auto text-xs font-medium font-[Manrope] px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400">Rank #{p.rank}</span>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] p-3">
                  <p className="text-[var(--text-muted)] text-[11px] uppercase tracking-widest font-[Manrope] flex items-center gap-1"><Star size={11} /> Level</p>
                  <p className="text-[var(--text-primary)] text-2xl font-bold font-[Outfit]">{p?.level ?? 0}</p>
                </div>
                <div className="rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] p-3">
                  <p className="text-[var(--text-muted)] text-[11px] uppercase tracking-widest font-[Manrope] flex items-center gap-1"><Coins size={11} /> {p?.currency_name || 'Coins'}</p>
                  <p className="text-[var(--text-primary)] text-2xl font-bold font-[Outfit]">{p?.currency_emoji || ''} {(p?.balance ?? 0).toLocaleString()}</p>
                </div>
              </div>
              <Bar value={p?.xp_into_level ?? 0} max={p?.xp_for_next ?? 0} />
              <p className="text-[var(--text-muted)] text-xs font-[Manrope] mt-1.5">
                {(p?.xp_into_level ?? 0).toLocaleString()} / {(p?.xp_for_next ?? 0).toLocaleString()} XP to next level
              </p>
            </div>

            <NowPlayingCard np={overview.now_playing} queue={overview.queue || []} requests={overview.requests} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <BirthdayCard guildId={selectedGuildId} initial={birthday} onChanged={setBirthday} />
            <Leaderboards data={boards} />
          </div>
        </>
      )}
    </PageShell>
  );
}
